import React from 'react';
import { AppBar as MuiAppBar, Toolbar, Button, Box, Chip, IconButton, Avatar, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeIcon from '@mui/icons-material/Home';
import BookIcon from '@mui/icons-material/Book';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logout } from '../services/authService';
import Logo from './Logo';

interface AppBarProps {
  onMenuClick?: () => void;
}

const AppBar: React.FC<AppBarProps> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, isGuest, user } = useAppSelector((state) => state.auth);

  const handleLogout = async () => {
    try {
      await logout(dispatch);
      navigate('/');
    } catch (err) {
      // Error handled in service
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <MuiAppBar
      position="sticky"
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      }}
    >
      <Toolbar
        sx={{
          py: 1,
          px: { xs: 2, sm: 3, md: 4 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: { xs: 56, sm: 64 },
        }}
      >
        {/* Left Section: Menu + Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flex: 1 }}>
          {onMenuClick && (
            <IconButton
              color="inherit"
              aria-label="menu"
              onClick={onMenuClick}
              sx={{
                mr: { xs: 0, sm: 1 },
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  background: 'rgba(99, 102, 241, 0.1)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box
            sx={{
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={() => navigate('/')}
          >
            <Logo variant="compact" />
          </Box>
        </Box>

        {/* Center Section: Navigation (hidden on mobile) */}
        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flex: 1,
              justifyContent: 'center',
            }}
          >
            <Button
              color="inherit"
              onClick={() => navigate('/')}
              startIcon={<HomeIcon />}
              sx={{
                px: 2.5,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                ...(isActive('/') && {
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  color: 'primary.light',
                }),
                '&:hover': {
                  background: 'rgba(99, 102, 241, 0.1)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Home
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate('/courses')}
              startIcon={<BookIcon />}
              sx={{
                px: 2.5,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                ...(isActive('/courses') && {
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  color: 'primary.light',
                }),
                '&:hover': {
                  background: 'rgba(99, 102, 241, 0.1)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Courses
            </Button>
          </Box>
        )}

        {/* Right Section: Auth Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flex: 1,
            justifyContent: 'flex-end',
          }}
        >
          {isAuthenticated ? (
            <>
              {!isMobile && (
                <>
                  <Button
                    color="inherit"
                    onClick={() => navigate('/dashboard')}
                    startIcon={<DashboardIcon />}
                    sx={{
                      px: 2.5,
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.95rem',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      ...(isActive('/dashboard') && {
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        color: 'primary.light',
                      }),
                      '&:hover': {
                        background: 'rgba(99, 102, 241, 0.1)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Dashboard
                  </Button>
                  {user?.isAdmin && (
                    <Button
                      color="inherit"
                      onClick={() => navigate('/admin')}
                      startIcon={<AdminPanelSettingsIcon />}
                      sx={{
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        background: isActive('/admin')
                          ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)'
                          : 'transparent',
                        border: isActive('/admin') ? '1px solid rgba(239, 68, 68, 0.3)' : 'none',
                        color: isActive('/admin') ? 'error.light' : 'text.primary',
                        '&:hover': {
                          background: 'rgba(239, 68, 68, 0.1)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Admin
                    </Button>
                  )}
                </>
              )}
              <Chip
                avatar={
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 32,
                      height: 32,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                    }}
                  >
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                }
                label={user?.email?.split('@')[0] || 'User'}
                sx={{
                  height: 40,
                  background: 'rgba(99, 102, 241, 0.1)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                  color: 'text.primary',
                  '& .MuiChip-label': {
                    px: 2,
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  },
                  '&:hover': {
                    background: 'rgba(99, 102, 241, 0.15)',
                    borderColor: 'rgba(99, 102, 241, 0.3)',
                  },
                }}
              />
              <Button
                color="inherit"
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
                variant="outlined"
                sx={{
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  borderColor: 'rgba(148, 163, 184, 0.3)',
                  color: 'text.primary',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderColor: 'rgba(239, 68, 68, 0.5)',
                    color: 'error.light',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {isMobile ? '' : 'Log Out'}
              </Button>
            </>
          ) : (
            <>
              {isGuest && (
                <Button
                  color="inherit"
                  onClick={() => navigate('/login')}
                  variant="outlined"
                  sx={{
                    px: 2.5,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    borderColor: 'rgba(148, 163, 184, 0.3)',
                    color: 'text.primary',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: 'rgba(99, 102, 241, 0.5)',
                      background: 'rgba(99, 102, 241, 0.1)',
                      color: 'primary.light',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {isMobile ? 'Sign Up' : 'Create Account'}
                </Button>
              )}
              <Button
                color="inherit"
                onClick={() => navigate('/login')}
                variant={isActive('/login') ? 'contained' : 'outlined'}
                startIcon={<AccountCircleIcon />}
                sx={{
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  ...(isActive('/login')
                    ? {
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                          boxShadow: '0 6px 16px rgba(99, 102, 241, 0.5)',
                        },
                      }
                    : {
                        borderColor: 'rgba(148, 163, 184, 0.3)',
                        color: 'text.primary',
                        '&:hover': {
                          borderColor: 'rgba(99, 102, 241, 0.5)',
                          background: 'rgba(99, 102, 241, 0.1)',
                          color: 'primary.light',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                        },
                      }),
                }}
              >
                {isMobile ? 'Login' : 'Log In'}
              </Button>
            </>
          )}
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar;
